/* eslint-disable react-hooks/exhaustive-deps */
import DocumentViewerModal from "components/DocumentViewer";
import FileUpload from "components/FileUpload";
import { useCourtFilingMarks } from "components/CourtFilingMarks";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { editCrimeReferences } from "services/incidentService";
import { fetchSuspect } from "services/lealIntityService";
import {
  addFurtherInvestigation,
  editFurtherInvestigation,
  editFurtherInvestigation2,
  editReceivingInvestigationFile2,
  fetchReceivingInvestigationFile,
} from "services/prosecutorPoliceService";
import {
  CrimeReference,
  FILE_URL,
  FurtherInvestigation,
  FurtherInvestigationDecision,
  InvestigationFile,
  Roles,
  Suspect,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm, hasRole } from "utils/helper";

type Props = {
  isOpen: boolean;
  action: "Add" | "Forward" | "Reject" | "Edit";
  file?: InvestigationFile;
  incident?: CrimeReference | null;
  furtherInvestigation?: FurtherInvestigation;
  onClose: (refetch: boolean) => void;
};

const defaultValue: FurtherInvestigation = {
  uu_id: 0,
  id: 0,
  suspects_ids: [],
  crime_registration_number: "",
  prosecutor_registration_number: "",
  incident_registration_number: "",
  suspect_condition: "",
  file_submitted_by: "",
  reason_for_request: "",
  requesting_body: "",
  date_requested: new Date(),
  investigation_file: null,
};

const FurtherInvestigationForm: FC<Props> = ({
  isOpen,
  action,
  file,
  incident,
  furtherInvestigation,
  onClose,
}) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const [formState, setFormState] = useState<FurtherInvestigation>(
    furtherInvestigation || defaultValue,
  );

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  // Office stamp and signature on the request forwarded to the police.
  const policeMarks = useCourtFilingMarks(
    isRegistrar &&
      action === "Forward" &&
      furtherInvestigation?.decision === "forward_to_registrar",
  );
  const isPolice = loginData?.role === "Member" || loginData?.is_police;

  const [suspects, setSuspects] = useState<Suspect[]>([]);
  const [suspectsIds, setSuspectsIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [requestFile, setRequestFile] = useState<File | null>(null);

  // Files already uploaded for this request (request doc + police result).
  // Only the latest uploaded file is shown in the modal.
  const latestUploadedDoc = ((furtherInvestigation?.documents || []) as any[])
    .slice()
    .sort(
      (a, b) =>
        new Date(b?.created_at || "").getTime() -
        new Date(a?.created_at || "").getTime(),
    )[0];

  const [investigationFile, setInvestigationFile] =
    useState<InvestigationFile>();

  useEffect(() => {
    if (file || furtherInvestigation) {
      fetchSuspect()
        .then((data) => {
          setSuspects(
            data?.filter(
              (d) =>
                d?.Incident_Registration_number ===
                (file?.incident_registration_number ||
                  furtherInvestigation?.incident_registration_number),
            ),
          );
        })
        .catch((error) => {
          showTooltip(
            error?.response?.data?.detail || errorHandlerOnForm(error),
            "error",
          );
        })
        .finally(() => {
          if (furtherInvestigation) {
            setFormState({
              ...furtherInvestigation,
              suspects_ids: furtherInvestigation?.suspects
                ? furtherInvestigation?.suspects?.map((i) => i?.uu_id) || []
                : [],
            });
            setSuspectsIds(
              furtherInvestigation?.suspects
                ? furtherInvestigation?.suspects?.map((i) => i?.uu_id) || []
                : [],
            );
          } else if (file) {
            setFormState({
              ...defaultValue,
              uu_id: file?.id || 0,
              crime_registration_number: file?.crime_reference_number || "",
              prosecutor_registration_number:
                file?.prosecutor_registration_number || "",
              incident_registration_number:
                file?.incident_registration_number || "",
              investigation_file: file?.id || null,
              police_station_id: file?.police_station_id || "",
              police_station_name: file?.police_station_name || "",
              is_efiling: file?.is_efiling || false,
              // Default the submitter to the logged-in user (editable).
              file_submitted_by: loginData?.full_name || "",
            });
          }
        });
    }
  }, [file, furtherInvestigation]);

  useEffect(() => {
    if (furtherInvestigation) {
      setLoading(true);

      fetchReceivingInvestigationFile()
        ?.then((data) => {
          if (data) {
            setInvestigationFile(
              data?.find(
                (item) =>
                  item.incident_registration_number ===
                  incident?.Incident_Registration_number,
              ) || undefined,
            );
          }
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false);
        });
    }
  }, [furtherInvestigation, incident]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formState.file_submitted_by) return t("file_submitted_by_required");
    else if (!formState.reason_for_request)
      return t("reason_for_request_required");
    else if (!formState.incident_registration_number)
      return t("incident_registration_number_required");
    else if (!formState.requesting_body) return t("requesting_body_required");
    else if (!formState.date_requested) return t("requested_date_required");

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    let newDecision = furtherInvestigation?.decision;

    switch (furtherInvestigation?.decision) {
      case "rejected":
      case "assigned_to_clerk":
        newDecision = "forward_to_coordinator";
        break;
      case "forward_to_coordinator":
        newDecision = "forward_to_registrar";
        break;
      case "forward_to_registrar":
        newDecision = "forward_to_police";
        break;
      case "forward_to_police":
        newDecision = "back_coordinator";
        break;
      case "back_coordinator":
        newDecision = "assigned_to_clerk";
        break;
      default:
        newDecision = "forward_to_coordinator";
    }

    setLoading(true);

    // Prosecutor's request file is attachable on both Add and Edit.
    const reqFile =
      action === "Add" || action === "Edit" ? requestFile : undefined;
    const resFile =
      action === "Forward" &&
      furtherInvestigation?.decision === "forward_to_police"
        ? attachmentFile
        : undefined;

    const base: any = {
      ...formState,
      date_requested: formState.date_requested,
      decision: newDecision,
    };
    // Nested collections must not be re-sent.
    delete base.suspects;
    delete base.feedbacks;
    delete base.documents;

    let submitPromise: Promise<any>;
    if (action === "Add") {
      // Create (POST) multipart: request file + selected suspects.
      submitPromise = addFurtherInvestigation({
        ...base,
        additional_investigation_file: reqFile ?? undefined,
      });
    } else if (
      reqFile ||
      resFile ||
      policeMarks.marks.stamp_file ||
      policeMarks.marks.signature_file
    ) {
      // PATCH with a file → multipart. suspects_ids aren't changed here and a
      // single value via multipart is mis-read by the API on update, so drop it.
      const rest = { ...base };
      delete rest.suspects_ids;
      submitPromise = editFurtherInvestigation({
        ...rest,
        ...(reqFile ? { additional_investigation_file: reqFile } : {}),
        ...(resFile
          ? { additional_investigation_result_file: resFile }
          : {}),
        ...policeMarks.marks,
      });
    } else {
      // No file → JSON PATCH (suspects_ids is sent as a real array).
      submitPromise = editFurtherInvestigation2(base);
    }

    submitPromise
      .then(() => {
        if (action === "Add" && file) {
          editReceivingInvestigationFile2({
            id: file?.id,
            status: "further_investigation_requested",
          });
        }

        if (
          furtherInvestigation?.decision === "forward_to_registrar" &&
          incident
        ) {
          editCrimeReferences(incident?.uu_id, {
            status: "pending",
            endDate: null,
          });
        }

        if (newDecision === "assigned_to_clerk" && investigationFile) {
          editReceivingInvestigationFile2({
            id: investigationFile?.id,
            status: "assigned_to_clerk",
          });
        }

        showTooltip(
          action === "Add"
            ? t("successfully_submitted")
            : t("successfully_updated"),
          "success",
        );
        setFormState(defaultValue);
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  const handleFile = (file: File | null) => {
    setAttachmentFile(file);
  };

  if (!isOpen) return null;

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-3xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
            <span className="text-xl">🔎</span>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {t("further_investigation")}
            </h2>
            <p className="text-xs text-gray-500">
              {t("further_investigation_request_subtitle", {
                defaultValue:
                  "Request additional investigation and attach the supporting file.",
              })}
            </p>
          </div>
        </div>

        {/* Latest attached file only */}
        {latestUploadedDoc && (
          <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-semibold text-gray-800 mb-2">
              {t("latest_file", { defaultValue: "Latest File" })}
            </p>
            <DocumentViewerModal
              documents={[latestUploadedDoc]}
              baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.FURTHER_INVESTIGATION}`}
              layout="list"
              hideEmptyFile={true}
            />
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          {isProsecutor && (!furtherInvestigation || action === "Edit") && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
              <div className="flex flex-col w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("requested_date")}
                </label>
                <input
                  type="date"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="date_requested"
                  value={
                    formState?.date_requested
                      ? new Date(formState.date_requested)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("file_submitted_by")}
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="file_submitted_by"
                  value={formState?.file_submitted_by}
                  onChange={handleChange}
                />
              </div>

              <div className="flex flex-col w-full sm:col-span-2">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("reason_for_request")}
                </label>
                <textarea
                  rows={4}
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300 resize-y"
                  name="reason_for_request"
                  value={formState?.reason_for_request}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col w-full sm:col-span-2">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("suspects")}
                </label>
                <select
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300 h-40"
                  name="suspects_ids"
                  value={suspectsIds.map(String)}
                  onChange={(e) => {
                    const options: any = Array.from(
                      e.target.selectedOptions,
                      (option) => option.value,
                    );
                    setSuspectsIds(options);
                    setFormState((prev) => ({
                      ...prev,
                      suspects_ids: options,
                    }));
                  }}
                  multiple
                >
                  {Array.isArray(suspects) &&
                    suspects.map((suspect) => (
                      <option key={suspect?.uu_id} value={suspect?.uu_id}>
                        {suspect.first_name} {suspect?.middle_name}{" "}
                        {suspect.last_name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="flex flex-col w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("suspect_condition")}
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="suspect_condition"
                  value={formState?.suspect_condition}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("requesting_body")}
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="requesting_body"
                  value={formState?.requesting_body}
                  onChange={handleChange}
                />
              </div>

              <div className="flex flex-col w-full sm:col-span-2">
                <FileUpload
                  label={t("further_investigation_request_file", {
                    defaultValue: "Further Investigation Request File",
                  })}
                  fileTypes="*"
                  required={false}
                  info={t("upload_files")}
                  onChange={(f) => setRequestFile(f)}
                />
              </div>
            </div>
          )}
          {isCoordinator &&
            furtherInvestigation?.decision === "forward_to_coordinator" && (
              <div className="grid grid-cols-1 gap-4 mt-4">
                <div className="flex flex-col w-full">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    {t("number_of_days_given")}
                  </label>
                  <input
                    type="number"
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="number_of_days_given"
                    value={formState?.number_of_days_given || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}
          {isRegistrar &&
            furtherInvestigation?.decision === "forward_to_registrar" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <div className="flex flex-col w-full">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    {t("letter_number_sent")}
                  </label>
                  <input
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="letter_number_sent"
                    value={formState?.letter_number_sent}
                    onChange={handleChange}
                  />
                </div>
                <div className="flex flex-col w-full">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    {t("date_sent_to_police")}
                  </label>
                  <input
                    type="date"
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="date_sent_to_police"
                    value={
                      formState?.date_sent_to_police
                        ? new Date(formState.date_sent_to_police)
                            .toISOString()
                            .split("T")[0]
                        : ""
                    }
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}
          {(isRegistrar || isPolice) &&
            furtherInvestigation?.decision === "forward_to_police" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <div className="flex flex-col w-full">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    {t("letter_number_received")}
                  </label>
                  <input
                    type="text"
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="letter_number_received"
                    value={formState?.letter_number_received}
                    onChange={handleChange}
                  />
                </div>
                <div className="flex flex-col w-full">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    {t("date_received_from_police")}
                  </label>
                  <input
                    type="date"
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="date_received_from_police"
                    value={
                      formState?.date_received_from_police
                        ? new Date(formState.date_received_from_police)
                            .toISOString()
                            .split("T")[0]
                        : ""
                    }
                    onChange={handleChange}
                  />
                </div>

                <div className="sm:col-span-2">
                  <FileUpload
                    label={t("upload_finding_files")}
                    fileTypes="*"
                    required={false}
                    info=""
                    onChange={handleFile}
                  />
                </div>
              </div>
            )}
          {isProsecutor && furtherInvestigation && action !== "Edit" && (
            <div className="grid grid-cols-1 gap-4 mt-4">
              <div className="flex flex-col mt-4">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("decision")}
                </label>
                <select
                  className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                  name="decision"
                  value={formState?.decision || ""}
                  onChange={handleChange}
                >
                  <option value={""}>{t("select_decision")}</option>
                  {Object.entries(FurtherInvestigationDecision).map(
                    ([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ),
                  )}
                </select>
              </div>
              <div className="flex flex-col w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("decision_reason")}
                </label>
                <textarea
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="decision_reason"
                  rows={4}
                  value={formState?.decision_reason}
                  onChange={handleChange}
                />
              </div>
            </div>
          )}

          {policeMarks.fields}

          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                        text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                            ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  {t("loading")}
                </div>
              ) : action === "Forward" ? (
                furtherInvestigation?.decision === "forward_to_police" ? (
                  t("add_result", { defaultValue: "Add Result" })
                ) : furtherInvestigation?.decision ===
                  "forward_to_coordinator" ? (
                  t("approve_and_forward_to_registrar", {
                    defaultValue: "Approve & Forward to Registrar",
                  })
                ) : furtherInvestigation?.decision ===
                  "forward_to_registrar" ? (
                  t("add_registration_number", {
                    defaultValue: "Add Registration Number",
                  })
                ) : furtherInvestigation?.decision === "back_coordinator" ? (
                  t("forward_to_clerk", { defaultValue: "Forward to Clerk" })
                ) : (
                  t("approve", { defaultValue: "Approve" })
                )
              ) : (
                `${t(action.toLowerCase())} ${t("further_investigation")}`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FurtherInvestigationForm;
